import { createTheme, alpha } from '@mui/material/styles';


export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0a192f',
      light: '#172a45',
      dark: '#020c1b',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f57c00',
      light: '#ff9800',
      dark: '#bb4d00',
      contrastText: '#000000',
    },
    accent: {
      main: '#4b6584',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f7fa',  // Light, soft background
      paper: '#ffffff',    // Cards/panels remain clean
    },
    text: {
      primary: '#0a192f',  // Navy as main text
      secondary: '#495057', // Muted gray
    },
    divider: '#dee2e6',
    warning: {
      main: '#ff9800',
    },
  },
  typography: {
    fontFamily: '"Lato", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.75rem',
      color: '#0a192f',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: '#0a192f',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#172a45',
    },
    h6: {
      fontWeight: 500,
      color: '#020c1b',
    },
    body1: {
      fontSize: '1rem',
      color: '#495057',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 0,
  },
  spacing: 8,
  transitions: {
    duration: {
      standard: 250,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f5f7fa',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.secondary.main, 0.12),
            color: theme.palette.secondary.main,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          padding: '8px 15px',
          transition: 'all 0.25s ease',
        },
        containedPrimary: {
          backgroundColor: '#0a192f',
          '&:hover': {
            backgroundColor: '#172a45',
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: '#ffa726',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a192f',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: 0
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderRadius: 0,
          boxShadow: '0 4px 12px rgba(10, 25, 47, 0.05)',
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: '1px solid lightgray',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.06)',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#f1f3f5',
          color: '#0a192f',
        },
        input: {
          color: '#0a192f',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#ced4da',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true
      }
    }
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#0a192f',
      light: '#172a45',
      dark: '#020c1b',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f57c00',
      light: '#ff9800',
      dark: '#bb4d00',
      contrastText: '#000000',
    },
    accent: {
      main: '#4b6584',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0a192f',  // Deep navy base
      paper: '#172a45',    // Slightly lifted card color
    },
    text: {
      primary: '#ffffff',
      secondary: '#aab8c2',
    },
    divider: '#1e2a3a',
    warning: {
      main: '#ff9800',
    },
  },
  typography: {
    fontFamily: '"Lato", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.75rem',
      color: '#ffffff',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: '#ffffff',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#e0e6ed',
    },
    h6: {
      fontWeight: 500,
      color: '#aab8c2',
    },
    body1: {
      fontSize: '1rem',
      color: '#aab8c2',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 0,
  },
  spacing: 8,
  transitions: {
    duration: {
      standard: 250,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0a192f',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          padding: '8px 15px',
          transition: 'all 0.25s ease',
        },
        containedPrimary: {
          backgroundColor: '#f57c00',
          '&:hover': {
            backgroundColor: '#bb4d00',
            boxShadow: '0 6px 12px rgba(10, 25, 47, 0.4)',
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: '#f57c00',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a192f',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#172a45',
          borderRadius: 0,
          border: '0.5px solid #1e2a3a',
          boxShadow: '0 4px 12px rgba(2, 12, 27, 0.5)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: '#1e2a3a',
          color: '#ffffff',
          border: '0.5px solid #1e2a3a',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#0f2238',
          color: '#ffffff',
        },
        input: {
          color: '#ffffff',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#33475b',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true
      }
    }
  },
});

